import { useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRefinementList } from 'react-instantsearch';
import { useTranslation } from 'next-i18next';
import Skeleton from 'react-loading-skeleton';

import { useUI } from '@context/GlobalUIContext';
import { useStoresUI } from '@context/StoresContext';

import Toggle from '@components/ui/Toggle';

import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import styles from './StoreAvailabilityToggle.module.scss';

const FavouriteStore = dynamic(() => import('./FavouriteStore'), {
  loading: () => <Skeleton width={100} />,
  ssr: false,
});

export const StoreAvailabilitySkeleton = () => (
  <div className={styles.wrapper}>
    <Toggle isDisabled isActive={false}>
      <span className={styles.textLine}>
        <Skeleton width={100} />
      </span>
    </Toggle>
  </div>
);

const StoreAvailabilityToggle = () => {
  const { t } = useTranslation(['searchResults', 'common']);
  const { favouriteStoreDetails } = useStoresUI();
  const { openStoresModal, displayStoresModal } = useUI();

  const { items, refine } = useRefinementList({
    attribute: AlgoliaFacets.StoreInventory,
    limit: 100,
  });

  const activeStoreFacet = items.find((item) => item.isRefined);
  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
  const applyStoreFacet = useCallback(
    (storeId: string) => {
      refine(storeId);
    },
    [refine]
  );
  const isStoreFacetActive =
    !!favouriteStoreId && activeStoreFacet?.label === favouriteStoreId;

  const modalFlag = useRef('initial');
  const handleToggle = () => {
    if (!favouriteStoreId) {
      openStoresModal();
      return;
    }

    modalFlag.current = 'handleToggle';
    applyStoreFacet(favouriteStoreId);
  };

  // Handles case where a store is applied in the modal, we want the facet to be applied
  useEffect(() => {
    // Skip the effect on initial render and when the wrapper is clicked
    if (
      modalFlag.current === 'initial' ||
      modalFlag.current === 'handleToggle'
    ) {
      modalFlag.current = favouriteStoreId;
      return;
    }

    // Skip when the store is being hydrated from cookie (previous value was undefined)
    if (modalFlag.current === undefined) {
      modalFlag.current = favouriteStoreId;
      return;
    }

    // Only run the effect when the modal closes
    if (displayStoresModal) {
      return;
    }

    // If a new store is selected, remove the old one and apply the new one
    else if (activeStoreFacet?.label !== favouriteStoreId) {
      if (activeStoreFacet?.label) {
        applyStoreFacet(activeStoreFacet?.label);
      }
      if (favouriteStoreId) {
        applyStoreFacet(favouriteStoreId);
      }
    }
  }, [
    displayStoresModal,
    favouriteStoreId,
    applyStoreFacet,
    activeStoreFacet?.label,
  ]);

  return (
    <div className={styles.wrapper}>
      <Toggle isActive={isStoreFacetActive} handleClick={handleToggle}>
        <span className={styles.textLine}>
          {t('searchResults:search.facets.storeAvailability.inStockAt')}{' '}
          <FavouriteStore />
        </span>
      </Toggle>
    </div>
  );
};
export default StoreAvailabilityToggle;
